import * as React from "react"
import {useEffect, useState} from "react"
import {DrawerSeeker} from "@ui-seeker/core"
import {useAppToast} from "@ui-seeker/app"
import {
	Box,
	Typography,
	Paper,
	Divider,
	TextField,
	Button,
	Grid,
	Slider,
	Stack,
	Alert,
	CircularProgress,
	Chip
} from "@mui/material"
import {postSubmit} from "@ui-seeker/utils"

import {BACKEND_APIS, PRODUCT_MATCH_TYPES} from "@constants"
import {useUserApp} from "@hooks"
import {
	selectedBatchForAutovalidate,
	batchesTableData,
	autovalidateParams,
	autovalidatePreview,
	isLoadingAutovalidatePreview
} from "../@signals"


// Component to display a single mapping (adapts to batch type)
const MappingCard = ({ mapping, type, batchType }) => {
	if (!mapping) return null

	const isWorst = type === 'worst'
	const title = isWorst ? 'Worst Auto-validated' : 'Best Excluded'
	const subtitle = isWorst
		? 'Lowest score that will be auto-validated'
		: 'Highest score that will be auto-excluded'
	const color = isWorst ? 'success' : 'error'

	// For raw2sku: Product -> SKU (source is product, target is SKU)
	// For sku2raw: SKU -> Product (source is SKU, target is product)
	const isRaw2Sku = batchType === 'raw2sku'
	// Both product↔product directions render the same pair; only which side anchored the
	// batch differs, and the preview reads the pair off the back's own/competitor fields.
	const isProductMatch = PRODUCT_MATCH_TYPES.includes(batchType)

	let sourceLabel, sourceName, sourceImage, sourceSubtitle, sourceUrl
	let targetLabel, targetName, targetImage, targetSubtitle, targetUrl
	if (isProductMatch) {
		sourceLabel = 'Own Product'
		sourceName = mapping.ownName
		sourceImage = mapping.ownImage
		sourceSubtitle = [mapping.brandName, mapping.ownSite].filter(Boolean).join(' • ')
		sourceUrl = mapping.ownUrl
		targetLabel = 'Competitor'
		targetName = mapping.competitorName
		targetImage = mapping.competitorImage
		targetSubtitle = mapping.competitorSite || null
		targetUrl = mapping.competitorUrl
	} else {
		sourceLabel = isRaw2Sku ? 'Product' : 'SKU'
		sourceName = isRaw2Sku ? mapping.productMapped : mapping.skuName
		sourceImage = isRaw2Sku ? mapping.productImage : mapping.skuImage
		sourceSubtitle = isRaw2Sku ? null : `${mapping.brandName} • ${mapping.siteName}`
		sourceUrl = isRaw2Sku ? mapping.productUrl : null

		targetLabel = isRaw2Sku ? 'SKU' : 'Product'
		targetName = isRaw2Sku ? mapping.skuName : mapping.productMapped
		targetImage = isRaw2Sku ? mapping.skuImage : mapping.productImage
		targetSubtitle = isRaw2Sku ? `${mapping.brandName} • ${mapping.siteName}` : null
		targetUrl = isRaw2Sku ? null : mapping.productUrl
	}

	return (
		<Paper elevation={0} sx={{ p: 2, bgcolor: `${color}.lighter`, border: 1, borderColor: `${color}.main` }}>
			<Typography variant="subtitle2" fontWeight="bold" color={`${color}.dark`} gutterBottom>
				{title}
			</Typography>
			<Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 2 }}>
				{subtitle}
			</Typography>

			<Box sx={{ display: 'flex', gap: 1, alignItems: 'center', overflow: 'hidden' }}>
				{/* Source Section */}
				<Box sx={{ flex: 1, display: 'flex', gap: 1, alignItems: 'center', minWidth: 0 }}>
					<Box
						component="img"
						src={sourceImage}
						alt={sourceName}
						sx={{
							width: 50,
							height: 50,
							flexShrink: 0,
							objectFit: 'contain',
							borderRadius: 1,
							bgcolor: 'white',
							border: 1,
							borderColor: 'divider'
						}}
					/>
					<Box sx={{ flex: 1, minWidth: 0, overflow: 'hidden' }}>
						<Typography variant="caption" color="text.secondary" display="block">
							{sourceLabel}
						</Typography>
						<Typography
							variant="body2"
							fontWeight="medium"
							title={sourceName}
							sx={{
								overflow: 'hidden',
								textOverflow: 'ellipsis',
								display: '-webkit-box',
								WebkitLineClamp: 2,
								WebkitBoxOrient: 'vertical',
								lineHeight: 1.3,
								fontSize: '0.875rem'
							}}
						>
							{sourceName}
						</Typography>
						{sourceSubtitle && (
							<Typography variant="caption" color="text.secondary" noWrap>
								{sourceSubtitle}
							</Typography>
						)}
						{sourceUrl && (
							<Typography
								variant="caption"
								color="primary"
								component="a"
								href={sourceUrl}
								target="_blank"
								rel="noopener noreferrer"
								sx={{
									textDecoration: 'none',
									'&:hover': { textDecoration: 'underline' },
									display: 'block',
									whiteSpace: 'nowrap'
								}}
							>
								View product ↗
							</Typography>
						)}
					</Box>
				</Box>

				{/* Arrow */}
				<Box sx={{
					display: 'flex',
					flexDirection: 'column',
					alignItems: 'center',
					px: 0.5,
					flexShrink: 0,
					gap: 0.3
				}}>
					<Typography variant="body1" color="text.secondary" sx={{ fontSize: '1.2rem' }}>
						→
					</Typography>
					<Chip
						label={`${(mapping.iaDistance * 100).toFixed(1)}%`}
						size="small"
						color={color}
						sx={{ fontSize: '0.65rem', height: 18, minWidth: 45 }}
					/>
					<Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.6rem', whiteSpace: 'nowrap' }}>
						gap: {(mapping.iaConfidenceGap * 100).toFixed(1)}%
					</Typography>
				</Box>

				{/* Target Section */}
				<Box sx={{ flex: 1, display: 'flex', gap: 1, alignItems: 'center', minWidth: 0 }}>
					<Box
						component="img"
						src={targetImage}
						alt={targetName}
						sx={{
							width: 50,
							height: 50,
							flexShrink: 0,
							objectFit: 'contain',
							borderRadius: 1,
							bgcolor: 'white',
							border: 1,
							borderColor: 'divider'
						}}
					/>
					<Box sx={{ flex: 1, minWidth: 0, overflow: 'hidden' }}>
						<Typography variant="caption" color="text.secondary" display="block">
							{targetLabel}
						</Typography>
						<Typography
							variant="body2"
							fontWeight="medium"
							title={targetName}
							sx={{
								overflow: 'hidden',
								textOverflow: 'ellipsis',
								display: '-webkit-box',
								WebkitLineClamp: 2,
								WebkitBoxOrient: 'vertical',
								lineHeight: 1.3,
								fontSize: '0.875rem'
							}}
						>
							{targetName}
						</Typography>
						{targetSubtitle && (
							<Typography variant="caption" color="text.secondary" noWrap>
								{targetSubtitle}
							</Typography>
						)}
						{targetUrl && (
							<Typography
								variant="caption"
								color="primary"
								component="a"
								href={targetUrl}
								target="_blank"
								rel="noopener noreferrer"
								sx={{
									textDecoration: 'none',
									'&:hover': { textDecoration: 'underline' },
									display: 'block',
									whiteSpace: 'nowrap'
								}}
							>
								View product ↗
							</Typography>
						)}
					</Box>
				</Box>
			</Box>
		</Paper>
	)
}

// Visual chart component using CSS
const ImpactChart = ({toValidate, toExclude, manualReview, total, isLoading, worstAutovalidated, bestExcluded, batchType}) => {
	const validatedPct = total > 0 ? (toValidate / total) * 100 : 0
	const excludedPct = total > 0 ? (toExclude / total) * 100 : 0
	const manualPct = total > 0 ? (manualReview / total) * 100 : 0
	
	return (
		<Box sx={{ width: '100%', position: 'relative' }}>
			{/* Loading overlay with subtle indicator */}
			{isLoading && (
				<Box
					sx={{
						position: 'absolute',
						top: 0,
						right: 0,
						zIndex: 1,
						bgcolor: 'info.main',
						color: 'white',
						px: 1.5,
						py: 0.5,
						borderRadius: 1,
						fontSize: '0.75rem',
						fontWeight: 'medium',
						display: 'flex',
						alignItems: 'center',
						gap: 0.5
					}}
				>
					<CircularProgress size={12} sx={{ color: 'white' }} />
					Updating...
				</Box>
			)}
			
			<Box sx={{ 
				display: 'flex', 
				height: 60, 
				borderRadius: 2, 
				overflow: 'hidden', 
				mb: 2,
				opacity: isLoading ? 0.6 : 1,
				transition: 'opacity 0.3s ease'
			}}>
				{validatedPct > 0 && (
					<Box
						sx={{
							width: `${validatedPct}%`,
							bgcolor: 'success.main',
							display: 'flex',
							alignItems: 'center',
							justifyContent: 'center',
							transition: 'width 0.6s cubic-bezier(0.4, 0, 0.2, 1)'
						}}
					>
						{validatedPct > 10 && (
							<Typography variant="body2" fontWeight="bold" color="white">
								{validatedPct.toFixed(1)}%
							</Typography>
						)}
					</Box>
				)}
				{excludedPct > 0 && (
					<Box
						sx={{
							width: `${excludedPct}%`,
							bgcolor: 'error.main',
							display: 'flex',
							alignItems: 'center',
							justifyContent: 'center',
							transition: 'width 0.6s cubic-bezier(0.4, 0, 0.2, 1)'
						}}
					>
						{excludedPct > 10 && (
							<Typography variant="body2" fontWeight="bold" color="white">
								{excludedPct.toFixed(1)}%
							</Typography>
						)}
					</Box>
				)}
				{manualPct > 0 && (
					<Box
						sx={{
							width: `${manualPct}%`,
							bgcolor: 'warning.main',
							display: 'flex',
							alignItems: 'center',
							justifyContent: 'center',
							transition: 'width 0.6s cubic-bezier(0.4, 0, 0.2, 1)'
						}}
					>
						{manualPct > 10 && (
							<Typography variant="body2" fontWeight="bold" color="white">
								{manualPct.toFixed(1)}%
							</Typography>
						)}
					</Box>
				)}
			</Box>
			
			<Grid container spacing={2} sx={{ 
				opacity: isLoading ? 0.6 : 1,
				transition: 'opacity 0.3s ease'
			}}>
				<Grid item xs={4}>
					<Paper elevation={0} sx={{ 
						p: 2, 
						bgcolor: 'success.lighter', 
						borderLeft: 4, 
						borderColor: 'success.main',
						transition: 'all 0.3s ease'
					}}>
						<Typography variant="caption" color="text.secondary" display="block">
							Auto-validate
						</Typography>
						<Typography variant="h5" fontWeight="bold" color="success.dark" sx={{
							transition: 'all 0.4s ease'
						}}>
							{toValidate}
						</Typography>
						<Typography variant="caption" color="text.secondary">
							{validatedPct.toFixed(1)}% of total
						</Typography>
					</Paper>
				</Grid>
				<Grid item xs={4}>
					<Paper elevation={0} sx={{ 
						p: 2, 
						bgcolor: 'error.lighter', 
						borderLeft: 4, 
						borderColor: 'error.main',
						transition: 'all 0.3s ease'
					}}>
						<Typography variant="caption" color="text.secondary" display="block">
							Auto-exclude
						</Typography>
						<Typography variant="h5" fontWeight="bold" color="error.dark" sx={{
							transition: 'all 0.4s ease'
						}}>
							{toExclude}
						</Typography>
						<Typography variant="caption" color="text.secondary">
							{excludedPct.toFixed(1)}% of total
						</Typography>
					</Paper>
				</Grid>
				<Grid item xs={4}>
					<Paper elevation={0} sx={{ 
						p: 2, 
						bgcolor: 'warning.lighter', 
						borderLeft: 4, 
						borderColor: 'warning.main',
						transition: 'all 0.3s ease'
					}}>
						<Typography variant="caption" color="text.secondary" display="block">
							Manual review
						</Typography>
						<Typography variant="h5" fontWeight="bold" color="warning.dark" sx={{
							transition: 'all 0.4s ease'
						}}>
							{manualReview}
						</Typography>
						<Typography variant="caption" color="text.secondary">
							{manualPct.toFixed(1)}% of total
						</Typography>
					</Paper>
				</Grid>
			</Grid>
			
			{/* Edge Cases Section */}
			{(worstAutovalidated || bestExcluded) && (
				<Box sx={{ mt: 3 }}>
					<Typography variant="subtitle2" color="text.secondary" gutterBottom>
						Edge Cases
					</Typography>
					<Stack spacing={2}>
						{worstAutovalidated && <MappingCard mapping={worstAutovalidated} type="worst" batchType={batchType} />}
						{bestExcluded && <MappingCard mapping={bestExcluded} type="best" batchType={batchType} />}
					</Stack>
				</Box>
			)}
		</Box>
	)
}


export default function AutovalidateDrawer() {
	const { showToast } = useAppToast()
	const { userData } = useUserApp()
	const [isSubmitting, setIsSubmitting] = useState(false)
	
	const isOpen = selectedBatchForAutovalidate.value !== null
	const batchData = isOpen
		? batchesTableData.value.find(b => b.batchID === selectedBatchForAutovalidate.value)
		: null
	
	const handleClose = () => {
		selectedBatchForAutovalidate.value = null
		autovalidatePreview.value = null
	}

	// Fetch preview on open and when params change (debounced, with stable primitive deps)
	useEffect(() => {
		if (!isOpen || !batchData) return

		let cancelled = false
		const timer = setTimeout(async () => {
			isLoadingAutovalidatePreview.value = true
			try {
				const response = await postSubmit(
					`${BACKEND_APIS.BATCH_ADMIN}/admin/batches/${batchData.batchID}/autovalidate/preview`,
					autovalidateParams.value
				)
				if (!cancelled) {
					autovalidatePreview.value = response
				}
			} catch (err) {
				if (!cancelled) {
					console.error('Failed to fetch autovalidate preview:', err)
					showToast.error('Failed to fetch preview')
				}
			} finally {
				if (!cancelled) {
					isLoadingAutovalidatePreview.value = false
				}
			}
		}, 500)

		return () => {
			cancelled = true
			clearTimeout(timer)
		}
	}, [
		isOpen,
		batchData?.batchID,
		autovalidateParams.value.minRateAutovalidate,
		autovalidateParams.value.minGapSecondCandidate,
		autovalidateParams.value.maxRateAutoexclude
	])
	
	const handleParamChange = (field, value) => {
		autovalidateParams.value = {
			...autovalidateParams.value,
			[field]: value
		}
	}
	
	const handleSubmit = async () => {
		if (!batchData) return
		
		setIsSubmitting(true)
		
		const payload = {
			...autovalidateParams.value,
			idUser: userData?.userID
		}
		
		console.log('userData:', userData)
		console.log('Sending autovalidate payload:', payload)
		
		try {
			await postSubmit(
				`${BACKEND_APIS.BATCH_ADMIN}/admin/batches/${batchData.batchID}/autovalidate`,
				payload
			)
			
			// Update batch autovalidated flag in table data
			batchesTableData.value = batchesTableData.value.map(batch => 
				batch.batchID === batchData.batchID 
					? { ...batch, autovalidated: true }
					: batch
			)
			
			showToast.success('Autovalidation started successfully')
			handleClose()
		} catch (err) {
			console.error('Failed to execute autovalidate:', err)
			showToast.error('Failed to execute autovalidation')
		} finally {
			setIsSubmitting(false)
		}
	}
	
	if (!isOpen || !batchData) return null
	
	const preview = autovalidatePreview.value || {
		toValidate: 0,
		toExclude: 0,
		manualReview: 0,
		total: batchData.pendingTasks || 0
	}
	
	return (
		<DrawerSeeker
			isOpen={isOpen}
			controller={handleClose}
			title={`Autovalidate Batch - ${batchData.name || `#${batchData.batchID}`}`}
			width="60%"
			successProps={{
				onClick: handleSubmit,
				disabled: isSubmitting || isLoadingAutovalidatePreview.value,
				children: isSubmitting ? "Running..." : "Run Autovalidation",
				color: "success"
			}}
			cancelProps={{
				disabled: isSubmitting
			}}
		>
			<Stack spacing={3}>
				{/* Info Alert */}
				<Alert severity="info">
					This process will autovalidate the batch mappings based on the configured parameters. Changes will be applied immediately.
				</Alert>
				
				{/* Batch Info */}
				<Paper elevation={0} sx={{ p: 2, bgcolor: 'background.default' }}>
					<Grid container spacing={2}>
						<Grid item xs={6}>
							<Typography variant="caption" color="text.secondary" display="block">
								Batch ID
							</Typography>
							<Typography variant="body1" fontWeight="medium">
								#{batchData.batchID}
							</Typography>
						</Grid>
						<Grid item xs={6}>
							<Typography variant="caption" color="text.secondary" display="block">
								Pending tasks
							</Typography>
							<Typography variant="body1" fontWeight="medium">
								{batchData.pendingTasks || 0}
							</Typography>
						</Grid>
					</Grid>
				</Paper>
				
				{/* Parameters Section */}
				<Paper elevation={0} sx={{ p: 3, bgcolor: 'background.default' }}>
					<Typography variant="h6" gutterBottom>
						Autovalidation Parameters
					</Typography>
					<Divider sx={{ mb: 3 }} />
					
					{/* Auto-validate section */}
					<Box sx={{ mb: 4 }}>
						<Typography variant="subtitle1" fontWeight="bold" gutterBottom color="success.main">
							Autovalidate Section
						</Typography>
						
						<Box sx={{ mb: 3 }}>
							<Typography variant="body2" gutterBottom>
								Min score to autovalidate
							</Typography>
							<Grid container spacing={2} alignItems="center">
								<Grid item xs={9}>
									<Slider
										value={autovalidateParams.value.minRateAutovalidate}
										onChange={(e, value) => handleParamChange('minRateAutovalidate', value)}
										min={0}
										max={1}
										step={0.01}
										marks={[
											{ value: 0, label: '0.0' },
											{ value: 0.5, label: '0.5' },
											{ value: 1, label: '1.0' }
										]}
										valueLabelDisplay="auto"
										color="success"
									/>
								</Grid>
								<Grid item xs={3}>
									<TextField
										type="number"
										value={autovalidateParams.value.minRateAutovalidate}
										onChange={(e) => handleParamChange('minRateAutovalidate', parseFloat(e.target.value) || 0)}
										inputProps={{ min: 0, max: 1, step: 0.01 }}
										size="small"
										fullWidth
									/>
								</Grid>
							</Grid>
							<Typography variant="caption" color="text.secondary">
								Mappings with score greater than or equal to this value will be autovalidated
							</Typography>
						</Box>
						
						<Box sx={{ mb: 2 }}>
							<Typography variant="body2" gutterBottom>
								Min gap vs second candidate
							</Typography>
							<Grid container spacing={2} alignItems="center">
								<Grid item xs={9}>
									<Slider
										value={autovalidateParams.value.minGapSecondCandidate}
										onChange={(e, value) => handleParamChange('minGapSecondCandidate', value)}
										min={0}
										max={0.5}
										step={0.01}
										marks={[
											{ value: 0, label: '0.0' },
											{ value: 0.25, label: '0.25' },
											{ value: 0.5, label: '0.5' }
										]}
										valueLabelDisplay="auto"
										color="success"
									/>
								</Grid>
								<Grid item xs={3}>
									<TextField
										type="number"
										value={autovalidateParams.value.minGapSecondCandidate}
										onChange={(e) => handleParamChange('minGapSecondCandidate', parseFloat(e.target.value) || 0)}
										inputProps={{ min: 0, max: 0.5, step: 0.01 }}
										size="small"
										fullWidth
									/>
								</Grid>
							</Grid>
							<Typography variant="caption" color="text.secondary">
								Maximum allowed difference between first and second candidate
							</Typography>
						</Box>
					</Box>
					
					<Divider sx={{ my: 3 }} />
					
					{/* Auto-exclude section */}
					<Box>
						<Typography variant="subtitle1" fontWeight="bold" gutterBottom color="error.main">
							Auto-exclude Section
						</Typography>
						
						<Box sx={{ mb: 2 }}>
							<Typography variant="body2" gutterBottom>
								Max score below which to auto-exclude
							</Typography>
							<Grid container spacing={2} alignItems="center">
								<Grid item xs={9}>
									<Slider
										value={autovalidateParams.value.maxRateAutoexclude}
										onChange={(e, value) => handleParamChange('maxRateAutoexclude', value)}
										min={0}
										max={1}
										step={0.01}
										marks={[
											{ value: 0, label: '0.0' },
											{ value: 0.5, label: '0.5' },
											{ value: 1, label: '1.0' }
										]}
										valueLabelDisplay="auto"
										color="error"
									/>
								</Grid>
								<Grid item xs={3}>
									<TextField
										type="number"
										value={autovalidateParams.value.maxRateAutoexclude}
										onChange={(e) => handleParamChange('maxRateAutoexclude', parseFloat(e.target.value) || 0)}
										inputProps={{ min: 0, max: 1, step: 0.01 }}
										size="small"
										fullWidth
									/>
								</Grid>
							</Grid>
							<Typography variant="caption" color="text.secondary">
								Mappings with score less than or equal to this value will be auto-excluded
							</Typography>
						</Box>
					</Box>
				</Paper>
				
				{/* Preview Section */}
				<Paper elevation={0} sx={{ p: 3, bgcolor: 'background.default' }}>
					<Typography variant="h6" gutterBottom>
						Impact Preview
					</Typography>
					<Divider sx={{ mb: 3 }} />
					
					<ImpactChart
						toValidate={preview.toValidate}
						toExclude={preview.toExclude}
						manualReview={preview.manualReview}
						total={preview.total}
						isLoading={isLoadingAutovalidatePreview.value}
						worstAutovalidated={preview.worstAutovalidated}
						bestExcluded={preview.bestExcluded}
						batchType={batchData.batchType || 'sku2raw'}
					/>
				</Paper>
				

			</Stack>
		</DrawerSeeker>
	)
}
